import { useEffect, useRef, useState } from "react";
import { Paper } from "@mui/material";
import Draggable from "react-draggable";
import { Client } from "@stomp/stompjs";
import { HEADER_KEYS, NON_EXISTING, WEB_SOCKET_DOMAIN } from "../../utility/constants";

import styles from "./styles";
import FloatingButton from "./floating-button";
import ChatHeader from "./header";
import ChatMessages from "./chat-messages";
import ChatInput from "./chat-input";
import { ChatMessage } from "../../interfaces/chatbotInterface";
import { getMockChatHistory } from "../../api/chatbot";
import { getLocalStorageItem } from "../../utility/helper";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [isWaitingForAI, setIsWaitingForAI] = useState(false);
  const [isDragged, setIsDragged] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const stompClient = useRef<Client | null>(null);
  const [sessionUuid] = useState(() => {
    const existingUuid = sessionStorage.getItem("chatbot-session-uuid");
    if (existingUuid) return existingUuid;

    const newUuid = crypto.randomUUID();
    sessionStorage.setItem("chatbot-session-uuid", newUuid);
    return newUuid;
  });

  useEffect(() => {
    const loadInitial = async () => {
      setIsMessagesLoading(true);
      const history = await getMockChatHistory(sessionUuid, 1, 20);
      setMessages(history);
      setIsMessagesLoading(false);
      requestAnimationFrame(() =>
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" })
      );
    };
    loadInitial();
    const client = new Client({
      webSocketFactory: () => new WebSocket(`${WEB_SOCKET_DOMAIN}/chat-ws`),
      connectHeaders: {
        [HEADER_KEYS.X_USER_ID]: getLocalStorageItem('uid'),
      },
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe(`/topic/chat-with-ai/${sessionUuid}`, (message) => {
          const receivedChatMessage = JSON.parse(message.body) as ChatMessage;
          handleChatMessageReceived(receivedChatMessage);
        });
      },
      onStompError: (frame) => {
        console.error("STOMP error:", frame.headers["message"]);
      },
      debug: () => {},
    });

    client.activate();
    stompClient.current = client;

    return () => {
      client.deactivate();
    };
  }, [sessionUuid]);

  const handleChatMessageToSend = async (text?: string) => {
    const userText = text || input;
    if (!userText.trim()) return;
    const message: ChatMessage = {
      recordId: NON_EXISTING,
      sessionUuid,
      senderType: "human",
      content: userText,
      isReserved: false,
    };
    sendChatMessage(message);
    setMessages((prev) => [
      ...prev,
      {
        recordId: NON_EXISTING,
        sessionUuid,
        senderType: "human",
        content: message.content,
        isReserved: false,
      }
    ]);
    setInput("");
    scrollToBottom();
    setIsWaitingForAI(true);
  };

  const handleChatMessageReceived = (chatMessage: ChatMessage) => {
    setMessages((prev) => [
      ...prev,
      {
        recordId: chatMessage.recordId,
        sessionUuid,
        senderType: chatMessage.senderType,
        content: chatMessage.content,
        isReserved: chatMessage.isReserved,
      }
    ]);
    scrollToBottom();
    setIsWaitingForAI(false);
  };

  const sendChatMessage = (message: ChatMessage) => {
    if (!stompClient.current || !stompClient.current.connected) {
      console.warn("⚠️ STOMP client not connected");
      return;
    }
    if (message.type === "options") {
      return;
    }
    stompClient.current.publish({
      destination: "/app/chat-with-ai.send",
      body: JSON.stringify(message),
    });
  };

  const loadMore = async () => {
    if (isMessagesLoading || !hasMore) return;
    setIsMessagesLoading(true);
    const prevScrollHeight = document.querySelector("#chatbot-messages")?.scrollHeight ?? 0;
    const prevScrollTop = document.querySelector("#chatbot-messages")?.scrollTop ?? 0;
    const newMessages = await getMockChatHistory(sessionUuid, page + 1, 20);
    if (newMessages.length < 20) setHasMore(false); setPage((p) => p + 1);
    setMessages((prev) => [...newMessages, ...prev]);
    requestAnimationFrame(() => {
      const container = document.querySelector("#chatbot-messages") as HTMLDivElement;
      if (container) {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
      }
    });
    setIsMessagesLoading(false);
  };

  const scrollToBottom = () => {
    requestAnimationFrame(() =>
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    );
  };

  return (
    <>
      <FloatingButton
        open={open}
        onClick={() => {
          setOpen(!open);
          setIsDragged(false);
          setFullscreen(false);
          scrollToBottom();
        }}
      />
      {open && (
        <Draggable
          handle="#draggable-chatbot"
          bounds="body"
          onDrag={() => setIsDragging(true)}
          onStop={() => {
            setIsDragging(false);
            setIsDragged(true);
          }}
        >
          <Paper
            elevation={4}
            sx={{
              ...styles.paper(fullscreen, isDragged),
              ...(isDragging && styles.draggingPaper),
            }}
          >
            <ChatHeader
              fullscreen={fullscreen}
              onToggleFullscreen={() => setFullscreen(!fullscreen)}
              onClose={() => {
                setOpen(false);
                setFullscreen(false);
                setIsDragged(false);
              }}
            />
            <ChatMessages
              sessionUuid={sessionUuid}
              messages={messages}
              setMessages={setMessages}
              onSend={handleChatMessageToSend}
              messagesEndRef={messagesEndRef}
              loading={isMessagesLoading}
              hasMore={hasMore}
              onLoadMore={loadMore}
            />
            <ChatInput
              value={input}
              onChange={setInput}
              onSend={handleChatMessageToSend}
              isWaitingForAI={isWaitingForAI}
            />
          </Paper>
        </Draggable>
      )}
    </>
  );
}